/** @jsxImportSource @emotion/react */
import { Button, Intent } from "@blueprintjs/core"
import { IconNames } from "@blueprintjs/icons"
import { useForm } from "react-hook-form"
import { createToaster } from "../../../../utils/createToaster"

import { InputWrapper, IntegerInput } from "../../../../components/rhf"
import InstrumentSuggest from "../../../Config/components/InstrumentSuggest"
import { InstrumentLight } from "../../../Config/types"
import { WaveSelect } from "../components/WaveSelect"
import useCreateManualShortLocate from "../hooks/useCreateManualShortLocate"
import {
  AmountInputWrapper,
  FooterActions,
  FormContainer,
  FormGroupStyled,
  IconWrapper,
  InputContainer,
  SelectWrapper,
} from "../ReplaceOrderModal.styled"
import { Broker } from "../types/brokers"
import { useEffect } from "react"
import useTradingInstruments from "../../../Config/hooks/useTradingInstruments"

interface CreateShortLocateFormProps {
  locateInstrumentQuery?: string
  locateQuantity?: number
  onSuccess: () => void
  onCancel: () => void
}

export interface ShortLocateForm {
  instrument: InstrumentLight
  broker: Broker
  quantity: number
}

const CreateShortLocateForm = ({
  locateInstrumentQuery,
  locateQuantity,
  onSuccess,
  onCancel,
}: CreateShortLocateFormProps) => {
  // Set up form with react-hook-form
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ShortLocateForm>({
    defaultValues: {
      instrument: undefined,
      broker: Broker.BOFA,
      quantity: locateQuantity,
    },
    mode: "onChange",
  })

  // Set up the mutation for creating a short locate
  const createShortLocateMutation = useCreateManualShortLocate({
    onSuccess: response => {
      createToaster({
        message: response.message || "Short locate created successfully",
        intent: Intent.SUCCESS,
      })
      onSuccess()
    },
    onError: error => {
      createToaster({
        message: `Error creating short locate`,
        intent: Intent.DANGER,
        timeout: 5000,
      })
    },
  })

  // Handle form submission
  const onSubmit = (data: ShortLocateForm) => {
    createShortLocateMutation.mutate({
      instrument_id: data.instrument.id,
      broker: data.broker,
      quantity: data.quantity,
    })
  }

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      {/* Instrument Search */}
      <FormGroupStyled
        label={
          <span>
            <IconWrapper icon={IconNames.SEARCH} />
            Instrument
          </span>
        }
        helperText={errors.instrument?.message}
        intent={errors.instrument ? Intent.DANGER : Intent.NONE}
      >
        <InstrumentSuggest
          name={`instrument`}
          control={control}
          rules={{
            required: "Ticker is required",
          }}
          initialInstrumentQuery={locateInstrumentQuery}
        />
      </FormGroupStyled>

      {/* Broker */}
      <FormGroupStyled
        label={
          <span>
            <IconWrapper icon={IconNames.OFFICE} />
            Broker
          </span>
        }
      >
        <SelectWrapper>
          <WaveSelect
            name="broker"
            optionsEnum={Broker}
            control={control as any}
            rules={{
              required: "Broker is required",
            }}
          />
        </SelectWrapper>
      </FormGroupStyled>

      {/* Quantity */}
      <FormGroupStyled
        label={
          <span>
            <IconWrapper icon={IconNames.NUMERICAL} />
            Quantity
          </span>
        }
      >
        <InputContainer>
          <AmountInputWrapper>
            <InputWrapper error={errors?.quantity}>
              <IntegerInput
                registerReturn={register("quantity", {
                  required: "Quantity is required",
                  min: {
                    value: 1,
                    message: "Minimum quantity is 1",
                  },
                })}
                min={1}
              />
            </InputWrapper>
          </AmountInputWrapper>
        </InputContainer>
      </FormGroupStyled>

      <div className="bp3-dialog-footer">
        <FooterActions>
          <Button onClick={onCancel} icon={IconNames.CROSS}>
            Cancel
          </Button>
          <Button
            type="submit"
            intent="primary"
            loading={createShortLocateMutation.isLoading}
            disabled={Object.keys(errors).length > 0}
            icon={IconNames.LOCATE}
          >
            Create Short Locate
          </Button>
        </FooterActions>
      </div>
    </FormContainer>
  )
}

export default CreateShortLocateForm
